'use client';

import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

import { ROUTES, STORAGE_KEY } from '@/constants';
import {
  clearOverlayHistoryEntry,
  hasOverlayHistoryEntry,
} from '@/hooks/useOverlayHistory';
import { useHistoryStore } from '@/stores/HistoryStore/useHistoryStore';
import { closeResponsiveDialog } from '@/stores/ResponsiveDialogStore';

const NOT_ALLOW_BACK_ROUTE = [
  ROUTES.AUTH,
  ROUTES.EASY_CERTIFICATION,
  ROUTES.EASY_CERTIFICATION_CALLBACK,
  ROUTES.LOGIN,
  ROUTES.CROSS_DOMAIN_AUTH,
];

export const useCustomRouter = () => {
  const router = useRouter();

  const customRouter = useMemo(() => {
    const replace = (
      href: string,
      options?: Exclude<Parameters<typeof router.push>[1], undefined>,
    ) => {
      //* 마커를 먼저 걷는다 — 호출부가 오버레이를 닫으면 그 back이 이 이동을 취소한다
      clearOverlayHistoryEntry();

      sessionStorage.setItem(STORAGE_KEY.REPLACE_MARK, 'true');
      sessionStorage.setItem(STORAGE_KEY.REPLACE_MARK_KB, 'true');

      return router.replace(href, options);
    };

    const push = (
      href: string,
      options?: Exclude<Parameters<typeof router.push>[1], undefined>,
    ) => {
      //* 오버레이가 push해 둔 마커 엔트리 위에서 이동하면 그 엔트리를 덮어쓴다.
      //* 남겨두면 뒤로가기 한 번이 같은 URL로 되돌아가 눌리지 않은 것처럼 보인다.
      if (hasOverlayHistoryEntry()) {
        return replace(href, options);
      }

      return router.push(href, options);
    };

    //* fallbackHref는 되감을 히스토리가 없을 때(딥링크·새 탭·인증 라우트 직전) 갈 곳이다.
    //* 대부분의 화면은 홈이 맞지만, 기능 안에서 눌리는 버튼은 그 기능의 허브로 보내야
    //* 사용자가 하던 일을 이어갈 수 있다.
    const back = (fallbackHref: string = ROUTES.HOME.pathname) => {
      // RN WebView container의 Android back도 window.router.back()을 호출한다.
      // 페이지 history fallback보다 ResponsiveDialog 닫기를 먼저 시도해야
      // 앱 back 버튼으로 열린 modal/drawer가 닫힌다.
      if (closeResponsiveDialog()) return;

      //* 인라인 오버레이가 쌓아 둔 마커는 fallback 이동이 아니라 pop으로 걷어야 한다 — 이동하면 오버레이가 열린 채 화면만 바뀐다
      if (hasOverlayHistoryEntry()) return router.back();

      const { historyIndex, customHistory } = useHistoryStore.getState();

      const isEmptyHistory = historyIndex < 1;
      const prevHistoryPath = customHistory.get(historyIndex - 1);
      const isNotAllowBackRoute = prevHistoryPath
        ? NOT_ALLOW_BACK_ROUTE.some((route) =>
            prevHistoryPath.includes(route.pathname),
          )
        : true;

      if (isEmptyHistory || isNotAllowBackRoute) {
        return replace(fallbackHref);
      }

      return router.back();
    };

    return {
      ...router,
      push,
      replace,
      back,
    };
  }, [router]);

  return customRouter;
};
